import { CircleUser, Pencil } from "lucide-react";
import {
  atualizarUsuario,
  buscarUsuario,
  uploadImagemUsuario,
} from "shared/services/api/usuario.api";
import { useEffect, useState } from "react";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import styles from "./MinhaConta.module.scss";
import { ModalEditarCampo } from "shared/components/modal/ModalEditarCampo";
import { maskCpfCnpj, maskTelefone } from "shared/utils/masks";

export const MinhaConta = () => {
  const [usuario, setUsuario] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState(false);

  const [openEditarCampo, setOpenEditarCampo] = useState(false);
  const [campoEditando, setCampoEditando] = useState<"nome" | "email" | "telefone" | "data">("nome");
  const [loadingCampo, setLoadingCampo] = useState(false);

  /* ================================
     CARREGAR USUÁRIO
  ================================= */

  useEffect(() => {
    async function carregarUsuario() {
      try {
        const user = await buscarUsuario();

        if (!user) return;

        setUsuario(user);
      } catch (error) {
        console.error(error);
      }
    }

    carregarUsuario();
  }, []);

  /* ================================
     UPLOAD
  ================================= */

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      alert("Selecione uma imagem");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("Imagem muito grande (máx 2MB)");
      return;
    }

    setLoading(true);

    try {
      await uploadImagemUsuario(file);

      const usuarioAtualizado = await buscarUsuario();

      setUsuario(usuarioAtualizado);
      setImgError(false);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || "Erro ao atualizar foto.");
    } finally {
      setLoading(false);
    }
  };

  /* ================================
     IMAGEM SEGURA
  ================================= */

  const imagemValida = !imgError && usuario?.avatarUrl && usuario.avatarUrl.startsWith("http")
    ? usuario.avatarUrl
    : "/imagens/foto.png";

  /* ================================
     DATA DE NASCIMENTO
  ================================= */

  const dataNascimentoFormatada = usuario?.datanascimento
    ? new Date(`${usuario.datanascimento}T00:00:00`).toLocaleDateString("pt-BR")
    : "-";

  /* ================================
     ABRIR MODAL
  ================================= */

  const abrirEdicaoCampo = (campo: "nome" | "email" | "telefone" | "data") => {
    setCampoEditando(campo);
    setOpenEditarCampo(true);
  };

  /* ================================
     VALOR DO CAMPO
  ================================= */

  const valorCampoEditando =
    campoEditando === "nome"
      ? usuario?.name || ""
      : campoEditando === "email"
        ? usuario?.email || ""
        : campoEditando === "telefone"
          ? usuario?.phoneNumber || ""
          : usuario?.datanascimento || "";

  /* ================================
     TÍTULO DO MODAL
  ================================= */

  const tituloCampoEditando =
    campoEditando === "nome"
      ? "Editar Nome"
      : campoEditando === "email"
        ? "Editar E-mail"
        : campoEditando === "telefone"
          ? "Editar Telefone"
          : "Editar Data de Nascimento";

  /* ================================
     TIPO DO CAMPO
  ================================= */

  const tipoCampoEditando =
    campoEditando === "nome"
      ? "nome"
      : campoEditando === "email"
        ? "email"
        : campoEditando === "telefone"
          ? "telefone"
          : "data";

  /* ================================
     SALVAR CAMPO
  ================================= */

  const salvarCampo = async (valor: string) => {
    try {
      setLoadingCampo(true);

      if (campoEditando === "nome") {
        await atualizarUsuario({
          name: valor,
        });
      }

      if (campoEditando === "email") {
        await atualizarUsuario({
          email: valor,
        });
      }

      if (campoEditando === "telefone") {
        await atualizarUsuario({
          telefone: valor.replace(/\D/g, ""),
        });
      }

      if (campoEditando === "data") {
        await atualizarUsuario({
          datanascimento: valor,
        });
      }

      const user = await buscarUsuario();

      setUsuario(user);
      setOpenEditarCampo(false);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || "Erro ao atualizar os dados.");
    } finally {
      setLoadingCampo(false);
    }
  };

  return (
    <>
      <PageContainer title="Minha Conta" showDesktopSearch={false} icon={<CircleUser size={18} />} showLogo
        logo={<img src="/imagens/menuLogoBranco.png" alt="Move" />}>
        <div className={styles.page}>
          <div className={styles.card}>

            {/* FOTO E NOME */}

            <div className={styles.fotoNome}>
              <div className={styles.avatarContainer}>
                <img src={imagemValida} alt="foto" className={styles.avatar} onError={() => setImgError(true)} />

                <input type="file" accept="image/*" id="uploadFoto" onChange={handleUpload} hidden />

                <label htmlFor="uploadFoto" className={styles.btnEdit}>
                  {loading ? "..." : <Pencil size={14} />}
                </label>
              </div>

              <div className={styles.nomeContainer}>
                <p>{usuario?.name || "-"}</p>

                <button type="button" className={styles.btnEditNome} onClick={() => abrirEdicaoCampo("nome")}>
                  <Pencil size={12} />
                </button>
              </div>
            </div>

            {/* LOADING FOTO */}

            {loading && <p style={{ textAlign: "center", fontSize: 12 }}>Atualizando foto...</p>}

            {/* E-MAIL */}

            <div className={styles.field}>
              <div className={styles.fieldContent}>
                <div>
                  <p>{usuario?.email || "-"}</p>
                  <label>E-mail</label>
                </div>

                <button type="button" className={styles.btnEditField} onClick={() => abrirEdicaoCampo("email")}>
                  <Pencil size={14} />
                </button>
              </div>
            </div>

            {/* TELEFONE */}

            <div className={styles.field}>
              <div className={styles.fieldContent}>
                <div>
                  <p>{usuario?.phoneNumber ? maskTelefone(usuario.phoneNumber) : "-"}</p>
                  <label>Telefone</label>
                </div>

                <button type="button" className={styles.btnEditField} onClick={() => abrirEdicaoCampo("telefone")}>
                  <Pencil size={14} />
                </button>
              </div>
            </div>

            {/* DATA DE NASCIMENTO */}

            <div className={styles.field}>
              <div className={styles.fieldContent}>
                <div>
                  <p>{dataNascimentoFormatada}</p>
                  <label>Data de Nascimento</label>
                </div>

                <button type="button" className={styles.btnEditField} onClick={() => abrirEdicaoCampo("data")}>
                  <Pencil size={14} />
                </button>
              </div>
            </div>

            {/* CPF - NÃO EDITÁVEL */}

            <div className={`${styles.field} ${styles.fieldReadonly}`}>
              <div className={styles.fieldContent}>
                <div>
                  <p>{usuario?.cpf ? maskCpfCnpj(usuario.cpf) : "-"}</p>
                  <label>CPF</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>

      {/* MODAL EDITAR CAMPO */}

      <ModalEditarCampo
        open={openEditarCampo}
        titulo={tituloCampoEditando}
        tipo={tipoCampoEditando}
        valorAtual={valorCampoEditando}
        loading={loadingCampo}
        onClose={() => setOpenEditarCampo(false)}
        onSave={salvarCampo}
      />
    </>
  );
};